/**
 * Which AI models a SCOPE may use — the shared presentational control behind
 * both the price book's plan editor and the platform console's per-tenant
 * Configuration screen, so an operator sees ONE layout, not two.
 *
 * It is deliberately PRESENTATIONAL: the caller injects `value` and an `onSave`
 * that performs the write and refreshes. That is what lets one component serve a
 * PLAN (writes `billing_plans.features.models`, in `@papan/billing`) and a
 * TENANT (writes `tenant_settings.allowed_models`, in `@papan/orgs`) without
 * either package importing the other — the same reason the metered-pricing and
 * LLM-pricing editors were unified. It lives in `@papan/core-ui`, which both
 * already depend on.
 *
 * Two states, deliberately not three: "All models" (stored `null`) and an
 * explicit set. An empty set is NOT a third state — it means unrestricted
 * everywhere in this system, so clearing every checkbox returns to "All".
 * Blocking every model is a budget cap of 0, not an empty list, which would
 * brick every agent turn for the scope. The empty-but-restricted state is
 * flagged BEFORE saving, because the save normalizes it to "All".
 *
 * Excluded models are HIDDEN from that scope's picker rather than shown as
 * restricted (docs/plans/2026-07-30_MODEL_ACCESS_GOVERNANCE.md).
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { MODEL_CATALOG, PROVIDER_TABS } from '@papan/types'
import { DirtyBar } from './DirtyBar'
import { useDraftState } from './draft-store'
import { useRegisterSection, type PlanEditBar } from './PlanEditBar'
import { pinnedBatch, type PinnedBatch } from '@papan/api-client'

/** Models an operator can choose from. Mock models are excluded: a QA affordance
 *  gated by an email allow-list, not a product decision made per plan/tenant. */
const SELECTABLE = MODEL_CATALOG.filter((m) => {
  const tab = PROVIDER_TABS.find((t) => t.id === m.provider)
  return !tab?.mockOnly
})

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|')
/** "Unchanged" exactly as `dirty` below reads it: same restriction, same set
 *  of models in any order (#897 — the draft store drops such a value, so a
 *  model unticked and ticked back leaves no hidden draft). */
const sameSelection = (a: string[] | null, b: string[] | null) =>
  a === null || b === null ? a === b : sameSet(a, b)

export function ModelAccessEditor({
  value,
  onSave,
  name,
  busy,
  bar,
  draftKey,
}: {
  /** `null` = unrestricted (all models). */
  value: string[] | null
  /** Perform the write with the normalized selection (empty ⇒ null) and refresh
   *  the source so `value` re-seeds the draft. Throwing surfaces the error. */
  onSave: (next: string[] | null) => Promise<void>
  /** Scope name, for the checkbox aria labels ("<name> — <model>"). */
  name: string
  busy?: boolean
  /** When present, register into the host's single Save bar and hide this one. */
  bar?: PlanEditBar
  /** #897 — names the unsaved selection (scope + item, e.g. `plan:<id>:models`)
   *  so it survives a phone/desktop layout swap. Absent = plain local state. */
  draftKey?: string
}) {
  const { t } = useTranslation('core-ui')
  // A draft over `value` (#897): while nothing is toggled it IS `value`, so a
  // reload of the source shows through; once toggled, a reload never overwrites
  // it. Save and Discard return it to `value`.
  const [draft, setDraft, { clear: clearDraft, settle: settleDraft }] = useDraftState<string[] | null>(draftKey ?? null, value, { isSame: sameSelection })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const restricted = draft !== null
  const dirty = !sameSelection(value, draft)

  const toggle = (id: string) => {
    setDraft((cur) => {
      // Toggling a model while unrestricted starts the list from the FULL
      // catalog minus that one — starting from `[id]` would silently strip
      // every other model, which is the opposite of what the click looks like.
      const base = cur ?? SELECTABLE.map((m) => m.id)
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id]
    })
  }

  // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it
  // (`batch` — the host bar's one batch, else its own); nothing is shown once that session ended
  const save = async (batch: PinnedBatch = pinnedBatch()) => {
    setSaving(true)
    setError(null)
    try {
      // An empty selection is "unrestricted", not "block everything" — see the
      // header. Normalized here so the UI never asks the caller to store a state
      // the server would reject.
      const sent = draft && draft.length > 0 ? draft : null
      await batch.run(() => onSave(sent))
      // Until `value` catches up (the batch reloads after every section) — or
      // already equals what was sent (an empty list is stored as "unrestricted").
      settleDraft(sent)
    } catch (e: any) {
      if (bar) throw e // let the host bar halt the batch and report
      if (!batch.ended()) setError(e?.message || t('settings.budget.models.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const discard = () => { clearDraft(); setError(null) }
  useRegisterSection(bar, `models:${name}`, dirty ? 1 : 0, save, discard)

  const disabled = !!busy || saving

  return (
    <div className="mt-2 pt-2 border-t themed-border">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <span className="text-[11px] themed-text-secondary font-medium">
          {t('settings.budget.models.title')}
        </span>
        <label className="flex items-center gap-1 text-[11px] themed-text-muted">
          <input
            type="checkbox"
            checked={!restricted}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.checked ? null : SELECTABLE.map((m) => m.id))}
            aria-label={`${name} — ${t('settings.budget.models.all')}`}
          />
          {t('settings.budget.models.all')}
        </label>
        {restricted && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300">
            {t('settings.budget.models.count', { count: draft!.length })}
          </span>
        )}
      </div>

      {restricted && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1.5">
          {SELECTABLE.map((m) => (
            <label key={m.id} className="flex items-center gap-1 text-[11px] themed-text-muted">
              <input
                type="checkbox"
                checked={draft!.includes(m.id)}
                disabled={disabled}
                onChange={() => toggle(m.id)}
                aria-label={`${name} — ${m.label}`}
              />
              {m.label}
            </label>
          ))}
        </div>
      )}

      {restricted && draft!.length === 0 && (
        // The state that reads as "block everything" and is not. Said before
        // saving, not after, because the save silently turns it into "All".
        <p className="text-[11px] text-amber-300 mb-1.5">{t('settings.budget.models.emptyMeansAll')}</p>
      )}

      {/* Own bar only when NOT driven by a host's single Save bar. */}
      {!bar && (
        <DirtyBar
          count={dirty ? 1 : 0}
          busy={disabled}
          onSave={() => void save()}
          onDiscard={discard}
          pendingLabel={t('settings.budget.models.unsaved', {
            defaultValue: 'Model access has unsaved changes.',
          })}
          discardLabel={t('settings.budget.pricing.discard')}
          saveLabel={t('common:save')}
          savingLabel={t('common:saving')}
        />
      )}

      {error && <p className="text-[11px] text-red-400 mt-1">{error}</p>}
    </div>
  )
}
